import { ICONS, type BadgeTone, type IconName } from "@sagot/ui";
import type { EndUserStatus, IdentityProviderType } from "../shared/types";

/** `AudienceIn.verification_method` values (backend `AudienceVerification`). */
export const VERIFICATION_METHODS = ["anonymous", "widget_jwt", "portal_sso", "google_workspace", "microsoft_entra", "email_otp", "roster"] as const;
export type VerificationMethod = (typeof VERIFICATION_METHODS)[number];

export function isVerificationMethod(value: string | null | undefined): value is VerificationMethod {
  return VERIFICATION_METHODS.some((method) => method === value);
}

interface MethodMeta {
  icon: IconName;
  tone: BadgeTone;
}

export const METHOD_META: Readonly<Record<VerificationMethod, MethodMeta>> = {
  anonymous: { icon: "eye-off", tone: "neutral" },
  widget_jwt: { icon: "key", tone: "brand" },
  portal_sso: { icon: "key", tone: "brand" },
  google_workspace: { icon: "shield-check", tone: "success" },
  microsoft_entra: { icon: "layers", tone: "success" },
  email_otp: { icon: "mail", tone: "warning" },
  roster: { icon: "hash", tone: "warning" },
};

/** Icon + chip colour per fixed audience code (overridden by the tenant's `icon` when valid). */
const CODE_ICON: Readonly<Record<number, { icon: IconName; box: string }>> = {
  1: { icon: "globe", box: "bg-sunken text-fg-muted" },
  2: { icon: "user-check", box: "bg-accent-soft text-accent-ink" },
  3: { icon: "users", box: "bg-primary-soft text-primary-ink" },
  4: { icon: "building", box: "bg-ink-950 text-white dark:bg-ink-100 dark:text-ink-950" },
};
const FALLBACK = { icon: "users" as IconName, box: "bg-sunken text-fg-muted" };

function isIconName(value: string | null | undefined): value is IconName {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(ICONS, value);
}

export function audienceIcon(code: number, icon: string | null | undefined): { icon: IconName; box: string } {
  const base = CODE_ICON[code] ?? FALLBACK;
  return isIconName(icon) ? { ...base, icon } : base;
}

/** Badge tone of an audience chip (end-user table, mappings). */
export function audienceTone(code: number): BadgeTone {
  if (code === 1) return "neutral";
  if (code === 2) return "warning";
  return "brand";
}

export const END_USER_STATUS: Readonly<Record<EndUserStatus, { tone: "success" | "neutral" | "danger"; icon: IconName }>> = {
  active: { tone: "success", icon: "check-circle" },
  verified: { tone: "success", icon: "shield-check" },
  invited: { tone: "neutral", icon: "mail" },
  blocked: { tone: "danger", icon: "x-circle" },
};

type StatusMeta = (typeof END_USER_STATUS)[EndUserStatus];

/** Badge meta for an end-user status (unknown values render as active). */
export function endUserStatusMeta(status: string): StatusMeta {
  const match = Object.entries(END_USER_STATUS).find(([key]) => key === status);
  return match ? match[1] : END_USER_STATUS.active;
}

/** Provider types an admin can create (roster is managed by the roster import). */
export const CREATABLE_PROVIDERS = ["portal_sso", "widget_jwt", "google_workspace", "microsoft_entra", "email_otp"] as const;
export type CreatableProvider = (typeof CREATABLE_PROVIDERS)[number];

export function isCreatable(type: IdentityProviderType): type is CreatableProvider {
  return CREATABLE_PROVIDERS.some((t) => t === type);
}

export const PROVIDER_ICON: Readonly<Record<IdentityProviderType, { icon: IconName; box: string }>> = {
  portal_sso: { icon: "key", box: "bg-primary-soft text-primary-ink" },
  widget_jwt: { icon: "code", box: "bg-primary-soft text-primary-ink" },
  google_workspace: { icon: "mail", box: "bg-success-soft text-success" },
  microsoft_entra: { icon: "layers", box: "bg-sunken text-fg-muted" },
  email_otp: { icon: "mail", box: "bg-accent-soft text-accent-ink" },
  // Deprecated by DEC-041 (SMS removed); kept only so legacy rows still render.
  mobile_otp: { icon: "mail", box: "bg-accent-soft text-accent-ink" },
  roster: { icon: "hash", box: "bg-accent-soft text-accent-ink" },
  // Secure launch (M29): the session the operator's own login opened; there is nothing to create or edit here.
  operator_launch: { icon: "key", box: "bg-primary-soft text-primary-ink" },
  // Operator OpenID sign-in (M29): configured under Settings > Secure launch; never listed here.
  operator_oidc: { icon: "key", box: "bg-primary-soft text-primary-ink" },
};
